import { useCallback, useState } from 'react';
import { getReportVersion } from '@encarpkg/api';
import { carReportUrl, setSessionItem, REPORT_ENTRY } from '@encarpkg/shared';
import { CAR_ID_FROM_DETAIL_TO_CAR_VERIFICATION } from 'app/constants/export';
import { setCookie } from 'app/libs/cookie';
import { loggingException } from 'app/libs/sentry';

/**
 * 이 버전일 때만 car 로 보낸다. 그 외·미확인은 fem 이 그대로 그린다.
 *
 * 문자열을 그대로 비교하지 않고 숫자로 읽는다 — 명세 설명은 `'2.0'` 인데 예시는 `2` 라
 * 둘 중 무엇이 와도 같게 판정해야 한다
 */
const VERSION_2 = 2;

export const isVersion2 = (reportVersion) => parseFloat(reportVersion) === VERSION_2;

/**
 * 수출 리드 진입 표식 — 이게 없으면 어느 버전이든 리포트가 열리지 않는다.
 *
 * **저장 위치가 버전마다 다르다.** 1.0(fem)은 같은 오리진에서 클라이언트가 sessionStorage 를 읽지만,
 * 2.0(car)은 다른 도메인의 SSR 이 판정해 sessionStorage 가 보이지 않는다 —
 * `.encar.com` 쿠키만 요청 헤더로 닿는다. 그래서 버전을 확인한 뒤에 알맞은 쪽에 남긴다
 */
const markGlobalEntry = (carId, isVersion2Car) => {
	const value = String(carId);

	if (isVersion2Car) setCookie(CAR_ID_FROM_DETAIL_TO_CAR_VERIFICATION, value);
	else setSessionItem(CAR_ID_FROM_DETAIL_TO_CAR_VERIFICATION, value);
};

/** 기본 이동. car 는 다른 오리진이라 `history.push` 로는 못 간다 */
const navigate = (url) => {
	window.location.href = url;
};

/**
 * 차량리포트 진입 — 버전을 확인해 알맞은 렌더러로 보낸다.
 *
 * fem 은 1.0 렌더러다. 2.0 차량이 여기로 들어오면 **조회가 실패하지 않고 디폴트 형태의 빈 리포트가
 * 200 으로 온다** — 그래서 `withCarVerificationReportAuth` 가 안내 후 화면을 닫는다.
 * 그 지경까지 가지 않도록 **진입 시점에** 버전을 확인해 2.0 이면 car 로 보낸다.
 *
 * 1.0 은 기존 동작을 그대로 쓴다 — 호출부가 `version1` 로 넘기고, **모르는 값이나 조회 실패도 그쪽으로 떨어진다.**
 * 현행 유지가 가장 안전한 폴백이다.
 */
export default function useReportRoute() {
	const [isLoading, setIsLoading] = useState(false);

	/**
	 * @param {object} params
	 * @param {string} params.entry - 진입 경로 (`REPORT_ENTRY`)
	 * @param {string|number} params.carId
	 * @param {object} [params.query] - 2.0 URL 에 실을 쿼리. 믿고 방문·배송은 신청 식별자가 필수다
	 * @param {string} [params.reportVersion] - 이미 알고 있는 버전. 넘기면 조회를 건너뛴다 (차량상세 응답에 실려 온다)
	 * @param {Function} params.version1 - 1.0 일 때 실행할 기존 동작
	 * @param {Function} [params.version2] - 2.0 일 때 URL 을 받아 이동. 새 창을 여는 진입점이 갈아끼운다
	 */
	const goReport = useCallback(async ({ entry, carId, query, reportVersion, version1, version2 = navigate }) => {
		setIsLoading(true);

		const isGlobalEntry = entry === REPORT_ENTRY.CAR_DETAIL_GLOBAL;

		try {
			// 차량상세는 `advertisement.verification.reportVersion` 으로 버전을 함께 준다 — 그러면 다시 묻지 않는다.
			// 값이 없는(NULL) 차량과 상세를 거치지 않은 진입점은 기존대로 조회한다
			const version = reportVersion ?? (await getReportVersion(carId)).data?.reportVersion;

			if (isVersion2(version)) {
				if (isGlobalEntry) markGlobalEntry(carId, true);

				version2(carReportUrl({ entry, carId, query }));
				return;
			}
		} catch (e) {
			loggingException(e);
		} finally {
			setIsLoading(false);
		}

		if (isGlobalEntry) markGlobalEntry(carId, false);

		version1();
	}, []);

	return { isLoading, goReport };
}
